import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Quote, Star } from "lucide-react";

const TrustVideo = () => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Force play attempt on mount (handles iOS strict autoplay)
    const tryPlay = () => {
      el.play().catch(() => {});
    };
    tryPlay();

    // Re-attempt on intersection
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tryPlay();
          }
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);

    // Re-attempt on first user interaction (iOS Safari sometimes needs this)
    const onFirstTouch = () => {
      tryPlay();
      window.removeEventListener("touchstart", onFirstTouch);
      window.removeEventListener("click", onFirstTouch);
    };
    window.addEventListener("touchstart", onFirstTouch, { passive: true });
    window.addEventListener("click", onFirstTouch);

    return () => {
      io.disconnect();
      window.removeEventListener("touchstart", onFirstTouch);
      window.removeEventListener("click", onFirstTouch);
    };
  }, []);

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  return (
    <div className="relative w-full h-full bg-[#050505] group">
      <video
        ref={ref}
        src="/assets/trust-video.mp4"
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
        poster="/assets/logo-pdr-france.jpeg"
        className="w-full h-full object-cover"
        data-testid="trust-video"
      />
      {/* Overlay border accents */}
      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#0055A4] pointer-events-none" />
      <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#EF4135] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#0055A4] pointer-events-none" />

      {/* Mute button */}
      <button
        onClick={toggleMute}
        data-testid="trust-video-mute"
        aria-label={muted ? "Activer le son" : "Couper le son"}
        className="absolute bottom-4 right-4 w-11 h-11 flex items-center justify-center bg-black/70 backdrop-blur-sm border border-white/30 hover:border-white text-white transition-colors z-10"
      >
        {muted ? (
          <VolumeX className="w-5 h-5" />
        ) : (
          <Volume2 className="w-5 h-5 text-[#007AFF]" />
        )}
      </button>

      {/* "LIVE" badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-black/70 backdrop-blur-sm border border-white/20 text-[10px] uppercase tracking-[0.3em] font-bold pointer-events-none">
        <span className="w-1.5 h-1.5 bg-[#EF4135] rounded-full animate-pulse" />
        En atelier
      </div>
    </div>
  );
};

export const Confiance = () => {
  return (
    <section
      id="confiance"
      data-testid="confiance-section"
      className="relative bg-[#0A0A0A] pt-28 md:pt-32 pb-20 md:pb-24 overflow-hidden border-b border-white/10"
    >
      <div className="absolute inset-0 grid-pattern opacity-15 pointer-events-none" />
      <div className="noise-overlay" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Big banner heading */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF]">
              — Notre engagement
            </div>
            <div className="flex items-center gap-1">
              <div className="h-[2px] w-6 bg-[#0055A4]" />
              <div className="h-[2px] w-6 bg-white" />
              <div className="h-[2px] w-6 bg-[#EF4135]" />
            </div>
          </div>
          <h2
            data-testid="confiance-title"
            className="font-heading font-black uppercase tracking-[-0.04em] leading-[0.85]"
          >
            <span className="block text-5xl sm:text-7xl lg:text-9xl xl:text-[10rem]">
              La confiance
            </span>
            <span className="block text-5xl sm:text-7xl lg:text-9xl xl:text-[10rem] text-white/30">
              avant tout
              <span className="inline-block w-4 h-4 md:w-6 md:h-6 ml-2 md:ml-4 bg-[#EF4135] align-baseline" />
            </span>
          </h2>
        </div>

        {/* Video + Reference card grid */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* VIDEO */}
          <div
            data-testid="trust-video-wrapper"
            className="lg:col-span-7 relative aspect-video lg:aspect-auto lg:min-h-[520px] border border-white/10 overflow-hidden bg-[#050505]"
          >
            <TrustVideo />
          </div>

          {/* TEXT + REFERENCE PHOTO */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Quote / message */}
            <div className="bg-[#111111] border border-white/10 p-8 md:p-10 relative">
              <Quote
                className="absolute -top-4 left-8 w-9 h-9 text-[#007AFF] bg-[#111111] p-1.5"
                strokeWidth={1.5}
              />
              <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6">
                Chaque véhicule qui passe entre nos mains repart avec sa
                <span className="text-white font-semibold"> peinture d'origine intacte</span>,
                sa garantie constructeur préservée et la valeur de revente
                protégée. Notre savoir-faire artisanal au service de votre
                tranquillité.
              </p>
              <div className="text-xs uppercase tracking-[0.3em] text-[#007AFF]">
                — PDR France
              </div>
            </div>

            {/* PRESTIGE REFERENCE PHOTO */}
            <a
              href="#realisations"
              data-testid="reference-prestige"
              className="relative group overflow-hidden border border-white/10 hover:border-[#EF4135]/60 transition-all bg-[#050505] aspect-[4/3]"
            >
              <img
                src="/assets/reference-denza.jpg"
                alt="Référence prestigieuse — Intervention PDR France sur Denza Z9 GT"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-[#EF4135] text-white text-[10px] uppercase tracking-[0.3em] font-bold">
                <Star className="w-3 h-3" fill="currentColor" />
                Référence prestige
              </div>
              <div className="absolute bottom-0 inset-x-0 p-5 md:p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">
                  Événement international
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight text-white leading-tight">
                  Denza Z9 GT
                </div>
                <div className="text-sm text-white/70 mt-1">
                  Intervention DSP sur véhicule premium · Carrosserie haute technicité
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#EF4135] group-hover:w-full transition-all duration-500" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
